import React, { useMemo, useState } from 'react';
import { Modal, Alert } from 'antd';
import { Button } from '@/components/Button';
import { QuestionPicker, isRecommendedRetry, lastAttemptByItemId } from '@/components/Interview/QuestionPicker';
import type { Assessment, TemplateItemHistory } from '@/services/api';
import styles from './RetakeModal.module.scss';

interface RetakeModalProps {
  assessment: Assessment;
  confirming?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: (selectedItemIds: string[], differentItemIds: string[]) => void;
}

/**
 * "Try Again" confirmation — lists every question in the
 * assessment's template with the low-scoring/unscored ones pre-selected, lets
 * the candidate adjust the selection (including "Select all"), then hands the
 * chosen subset back to the caller to create the retry sitting.
 */
export const RetakeModal: React.FC<RetakeModalProps> = ({
  assessment,
  confirming = false,
  error = null,
  onCancel,
  onConfirm,
}) => {
  const items = assessment.interviewTemplate.items;

  // Keyed on the candidate's latest attempt at each question in any sitting,
  // not on this sitting's own responses — a retry only covers the questions it
  // redid, so from the second retry onwards this sitting says nothing about
  // the ones it left out.
  const history = useMemo<TemplateItemHistory>(
    () => ({
      itemScoreHistory: assessment.itemScoreHistory ?? [],
      substitutionAvailability: assessment.substitutionAvailability ?? {},
    }),
    [assessment.itemScoreHistory, assessment.substitutionAvailability]
  );

  const [selectedIds, setSelectedIds] = useState<string[]>(() => {
    const lastAttempts = lastAttemptByItemId(history.itemScoreHistory);
    return items.filter((item) => isRecommendedRetry(lastAttempts.get(item.id))).map((item) => item.id);
  });

  // Which selected items should get a fresh question instead of a repeat —
  // defaults to "Same question" for everything, matching behavior before
  // this control existed.
  const [differentIds, setDifferentIds] = useState<string[]>([]);

  return (
    <Modal
      open
      closable={!confirming}
      maskClosable={!confirming}
      onCancel={onCancel}
      footer={null}
      width={680}
      centered
      className={styles.modal}
    >
      <h3 className={styles.title}>Retake this assessment</h3>
      <p className={styles.subtitle}>
        We&apos;ve pre-selected the questions that could use another attempt. Adjust the list, then confirm.
      </p>

      {error && <Alert type="error" message={error} showIcon className={styles.errorAlert} />}

      <QuestionPicker
        items={items}
        history={history}
        selectedIds={selectedIds}
        differentIds={differentIds}
        onChange={(nextSelected, nextDifferent) => {
          setSelectedIds(nextSelected);
          setDifferentIds(nextDifferent);
        }}
        showQuestionText
        listClassName={styles.list}
      />

      <div className={styles.actions}>
        <Button appearance="outline" onClick={onCancel} disabled={confirming}>
          Cancel
        </Button>
        <Button
          appearance="solid"
          onClick={() => onConfirm(selectedIds, differentIds)}
          loading={confirming}
          disabled={selectedIds.length === 0}
        >
          Re-try{selectedIds.length > 0 ? ` (${selectedIds.length} question${selectedIds.length === 1 ? '' : 's'})` : ''}
        </Button>
      </div>
    </Modal>
  );
};

export default RetakeModal;
